import React, { useState, useEffect } from "react";
import { storage } from "../firebase/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

function MaterialRFQ() {
  useEffect(() => {
    document.title =
      "Top Material Procurement for Construction & Interior Solutions";
  }, []);

  const [formData, setFormData] = useState({
    fullName: "",
    companyName: "",
    city: "",
    contactNumber: "",
    email: "",
    projectLocation: "",
    preferredBrands: "",
    deliveryTimeline: "",
    additionalNotes: "",
  });
  const [materials, setMaterials] = useState([
    { category: "", quantity: "", otherMaterial: "" },
  ]);
  const [file, setFile] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleMaterialChange = (index, e) => {
    const { name, value } = e.target;
    const newMaterials = [...materials];
    newMaterials[index][name] = value;

    // If material category changes to non-"Others", clear the otherMaterial field
    if (name === "category" && value !== "Others") {
      newMaterials[index].otherMaterial = "";
    }

    setMaterials(newMaterials);
  };

  const handleAddMaterial = () => {
    setMaterials([
      ...materials,
      { category: "", quantity: "", otherMaterial: "" },
    ]);
  };

  const handleRemoveMaterial = (index) => {
    const newMaterials = [...materials];
    newMaterials.splice(index, 1);
    setMaterials(newMaterials);
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const uploadFile = async (file, fullName) => {
    if (!file) return null;

    try {
      const sanitizedName = fullName.replace(/\s+/g, "_").toLowerCase();
      const fileName = `${sanitizedName}_${Date.now()}_${file.name}`;
      const storageRef = ref(storage, `RFQ/${fileName}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      console.error("Error uploading file:", error);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate at least one material is fully filled
    const isValidMaterials = materials.every(
      (material) =>
        material.category &&
        material.quantity &&
        (material.category !== "Others" || material.otherMaterial)
    );

    if (!isValidMaterials) {
      alert("Please fill all material details for each item");
      setIsSubmitting(false);
      return;
    }

    try {
      let fileUrl = null;
      if (file) {
        fileUrl = await uploadFile(file, formData.fullName);
      }

      const scriptUrl =
        "https://script.google.com/macros/s/AKfycbzGIM0zH4_ixTTg_K0jucJolzn54iGFvf1a7MFOM_bPitlb2GsKV6-tFwKgyoZUf3y7/exec";

      const submissionData = {
        ...formData,
        materials: materials, // Include all materials with quantities
        boqOrSpecification: fileUrl || "No file uploaded",
        timestamp: new Date().toISOString(),
      };

      const response = await fetch(scriptUrl, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(submissionData),
      });

      setSubmitted(true);
      setFormData({
        fullName: "",
        companyName: "",
        city: "",
        contactNumber: "",
        email: "",
        projectLocation: "",
        preferredBrands: "",
        otherBrand: "",
        deliveryTimeline: "",
        additionalNotes: "",
      });
      setMaterials([{ category: "", quantity: "", otherMaterial: "" }]);
      setFile(null);
    } catch (error) {
      console.error("Error:", error);
      alert("There was an error submitting the form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md mt-10 text-center">
        <h2 className="text-2xl font-bold text-green-600 mb-4">
          Thank you for your request
        </h2>
        <p className="text-gray-700">
          Our procurement team will review your submission and get back to you
          shortly.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md mt-10">
      <h1 className="text-2xl font-bold text-gray-800 mb-2">
        Request for Quotation (RFQ) – Material Procurement
      </h1>
      <p className="text-gray-600 mb-6">
        Please fill out the form below to request a quotation for materials. Our
        procurement team will respond within 24–48 hours with pricing and
        availability details.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Full Name */}
        <div>
          <label
            htmlFor="fullName"
            className="block text-sm font-medium text-gray-700"
          >
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="fullName"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
          />
        </div>

        {/* Company Name */}
        <div>
          <label
            htmlFor="companyName"
            className="block text-sm font-medium text-gray-700"
          >
            Company Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="companyName"
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
          />
        </div>

        {/* City */}
        <div>
          <label
            htmlFor="city"
            className="block text-sm font-medium text-gray-700"
          >
            City <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="city"
            name="city"
            value={formData.city}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
          />
        </div>

        {/* Contact Number */}
        <div>
          <label
            htmlFor="contactNumber"
            className="block text-sm font-medium text-gray-700"
          >
            Contact Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            id="contactNumber"
            name="contactNumber"
            value={formData.contactNumber}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
          />
        </div>

        {/* Email */}
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700"
          >
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
          />
        </div>

        {/* Project Location */}
        <div>
          <label
            htmlFor="projectLocation"
            className="block text-sm font-medium text-gray-700"
          >
            Project Name / Site Location <span className="text-red-500">*</span>
          </label>
          <textarea
            id="projectLocation"
            name="projectLocation"
            value={formData.projectLocation}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
            rows={3}
          />
        </div>

        {/* Materials Section */}
        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-700">
            Materials Required <span className="text-red-500">*</span>
          </label>

          {materials.map((material, index) => (
            <div key={index} className="border p-4 rounded-md space-y-3">
              <div className="flex justify-between items-center">
                <h3 className="font-medium">Material #{index + 1}</h3>
                {materials.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveMaterial(index)}
                    className="text-red-500 text-sm"
                  >
                    Remove
                  </button>
                )}
              </div>

              {/* Material Category */}
              <div>
                <label
                  htmlFor={`materialCategory-${index}`}
                  className="block text-sm font-medium text-gray-700"
                >
                  Material Category <span className="text-red-500">*</span>
                </label>
                <select
                  id={`materialCategory-${index}`}
                  name="category"
                  value={material.category}
                  onChange={(e) => handleMaterialChange(index, e)}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
                >
                  <option value="">Select a category</option>
                  <option value="Cement">Cement</option>
                  <option value="Steel">Steel</option>
                  <option value="Sand & Aggregates">Sand & Aggregates</option>
                  <option value="Electricals">Electricals</option>
                  <option value="Plumbing">Plumbing</option>
                  <option value="Tiles & Flooring">Tiles & Flooring</option>
                  <option value="Paints & Finishes">Paints & Finishes</option>
                  <option value="Doors & Windows">Doors & Windows</option>
                  <option value="Others">Others</option>
                </select>
              </div>

              {/* Other Material (conditional) */}
              {material.category === "Others" && (
                <div>
                  <label
                    htmlFor={`otherMaterial-${index}`}
                    className="block text-sm font-medium text-gray-700"
                  >
                    Please specify material{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id={`otherMaterial-${index}`}
                    name="otherMaterial"
                    value={material.otherMaterial}
                    onChange={(e) => handleMaterialChange(index, e)}
                    required={material.category === "Others"}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
                  />
                </div>
              )}

              {/* Quantity */}
              <div>
                <label
                  htmlFor={`quantity-${index}`}
                  className="block text-sm font-medium text-gray-700"
                >
                  Quantity Required <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id={`quantity-${index}`}
                  name="quantity"
                  value={material.quantity}
                  onChange={(e) => handleMaterialChange(index, e)}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
                />
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={handleAddMaterial}
            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            + Add Another Material
          </button>
        </div>

        {/* Preferred Brands */}
        <div>
          <label
            htmlFor="preferredBrands"
            className="block text-sm font-medium text-gray-700"
          >
            Preferred Brands (if any)
          </label>
          <select
            id="preferredBrands"
            name="preferredBrands"
            value={formData.preferredBrands}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
          >
            <option value="">Select preferred brand (optional)</option>
            <option value="Tata">Tata</option>
            <option value="JSW">JSW</option>
            <option value="Ultratech">Ultratech</option>
            <option value="Asian Paints">Asian Paints</option>
            <option value="Berger Paints">Berger Paints</option>
            <option value="Ashirwad Pipes">Ashirwad Pipes</option>
            <option value="Ramco">Ramco</option>
            <option value="Peena Cement">Peena Cement</option>
            <option value="Vizag Steel">Vizag Steel</option>
            <option value="Others">Others</option>
          </select>

          {/* Show additional input field if "Others" is selected */}
          {formData.preferredBrands === "Others" && (
            <div className="mt-2">
              <label
                htmlFor="otherBrand"
                className="block text-sm font-medium text-gray-700"
              >
                Please specify brand
              </label>
              <input
                type="text"
                id="otherBrand"
                name="otherBrand"
                value={formData.otherBrand || ""}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
              />
            </div>
          )}
        </div>

        {/* Delivery Timeline */}
        <div>
          <label
            htmlFor="deliveryTimeline"
            className="block text-sm font-medium text-gray-700"
          >
            Delivery Timeline <span className="text-red-500">*</span>
          </label>
          <select
            id="deliveryTimeline"
            name="deliveryTimeline"
            value={formData.deliveryTimeline}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
          >
            <option value="">Select timeline</option>
            <option value="Immediate (1-3 days)">Immediate (1-3 days)</option>
            <option value="Within a week">Within a week</option>
            <option value="Within 15 days">Within 15 days</option>
            <option value="Flexible / Discuss">Flexible / Discuss</option>
          </select>
        </div>

        {/* File Upload */}
        <div>
          <label
            htmlFor="file"
            className="block text-sm font-medium text-gray-700"
          >
            Upload BOQ or Specifications (optional)
          </label>
          <input
            type="file"
            id="file"
            onChange={handleFileChange}
            className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </div>

        {/* Additional Notes */}
        <div>
          <label
            htmlFor="additionalNotes"
            className="block text-sm font-medium text-gray-700"
          >
            Additional Notes / Requirements
          </label>
          <textarea
            id="additionalNotes"
            name="additionalNotes"
            value={formData.additionalNotes}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
            rows={3}
          />
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              isSubmitting ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isSubmitting ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Processing...
              </>
            ) : (
              "Submit Request"
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default MaterialRFQ;
